import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CtaButton, PageContentLayout } from '@tassomutuo-backoffice/ui';
import { getUserInLocalStorage } from '../../services/localStorage/localStorage';
import {
  createIntranetUserWithTassoProfile,
  getIntranetUsers,
  putTassomutuoProfile,
  requestProfileUpdate,
} from '../../services/api/profile/profile';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Box, Paper } from '@mui/material';
import { ProfileInputsForm, GoogleCalendarConnection } from '../../components';
import { toast } from 'react-toastify';

interface IFormInputs {
  email: string;
  name: string;
  lastName: string;
  provinces: string[];
  createdBy: string;
  profileImage: File | string | null;
  htmlDescription: string;
  published: boolean;
}

const ProfilePage = () => {
  const queryClient = useQueryClient();
  const email = getUserInLocalStorage()?.email;
  const methods = useForm<IFormInputs>();
  const { handleSubmit, reset } = methods;

  const [isLoading, setIsLoading] = useState(false);

  const {
    isPending,
    data: userData,
    error,
  } = useQuery({
    queryKey: ['IntranetUsers', email],
    queryFn: () =>
      getIntranetUsers({
        page: 1,
        pageSize: 1,
        email: email || '',
      }),
  });

  const putProfileMutation = useMutation({
    mutationFn: putTassomutuoProfile,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['Profiles'],
      });
      queryClient.invalidateQueries({
        queryKey: ['IntranetUsers'],
      });
      toast.success('Profilo aggiornato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const createIntranetUserWithTassoProfileMutation = useMutation({
    mutationFn: createIntranetUserWithTassoProfile,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['Profiles'],
      });
      queryClient.invalidateQueries({
        queryKey: ['IntranetUsers'],
      });
      toast.success('Utenza creata!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const intranetUser =
    userData?.results[0] && userData?.results[0].email === email
      ? userData?.results[0]
      : null;

  const profileIsPublished =
    intranetUser?.tassomutuoProfile?.published || false;

  useEffect(() => {
    if (intranetUser || email) {
      reset({
        email: intranetUser?.email || email,
        name: intranetUser?.name || '',
        lastName: intranetUser?.lastName || '',
        provinces: intranetUser?.tassomutuoProfile?.provinces || [],
        profileImage: intranetUser?.tassomutuoProfile?.profileImageUrl || null,
        htmlDescription: intranetUser?.tassomutuoProfile?.htmlDescription || '',
      });
    }
  }, [intranetUser, email]);

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    setIsLoading(true);
    try {
      if (!intranetUser?.id) {
        await createIntranetUserWithTassoProfileMutation.mutateAsync({
          ...data,
          createdBy: 'user',
          published: false,
          email: email || '',
        });
      } else {
        await putProfileMutation.mutateAsync({
          ...data,
          intranetUserId: intranetUser.id,
          createdBy: 'user',
          published: false,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onRequestUpdateClick = async () => {
    try {
      setIsLoading(true);
      await requestProfileUpdate();
      toast.success('Richiesta di modifica inviata, verrai contattato.');
    } catch {
      toast.error('Si è verificato un errore, riprova!');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContentLayout isLoading={isPending} title="Profilo" error={error}>
      <FormProvider {...methods}>
        <Paper
          sx={{ p: 4, maxWidth: '800px' }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          {!isPending && (
            <ProfileInputsForm
              profileToUpdate={intranetUser}
              adminMode={false}
              disabled={profileIsPublished}
            />
          )}
          {!profileIsPublished && (
            <Box display="flex" justifyContent="center" my={4}>
              <CtaButton variant="contained" type="submit" loading={isLoading}>
                SALVA
              </CtaButton>
            </Box>
          )}

          {profileIsPublished && (
            <Box display="flex" justifyContent="center" my={4}>
              <CtaButton
                variant="contained"
                type="button"
                loading={isLoading}
                onClick={onRequestUpdateClick}
              >
                Richiedi modifica
              </CtaButton>
            </Box>
          )}
        </Paper>
      </FormProvider>

      <Box sx={{ maxWidth: '800px', mt: 3 }}>
        <GoogleCalendarConnection />
      </Box>
    </PageContentLayout>
  );
};

export default ProfilePage;
